import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X } from 'lucide-react';
import SiteHeader from '@/components/website/SiteHeader';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker using local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const ProtectedDocumentViewer = () => {
  const [searchParams] = useSearchParams();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth - 24 : 320);
  const containerRef = useRef(null);
  const pagesRef = useRef({}); // pageNumber -> DOM node (mobile scroll spy)

  const docPath = searchParams.get('doc');
  const title = searchParams.get('title') || 'Document';
  const backPath = searchParams.get('back') || '/documents';

  // Track viewport size — switch UX mode + adjust page width
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
      const w = containerRef.current?.clientWidth || window.innerWidth - 24;
      setContainerWidth(Math.min(w, 1000));
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [numPages]);

  // Right-click / keyboard-shortcut protection
  useEffect(() => {
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['p', 's', 'P', 'S'].includes(e.key)) { e.preventDefault(); return false; }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // In mobile scroll mode, keep the toolbar's page counter in sync with scroll position.
  useEffect(() => {
    if (!isMobile || !numPages) return;
    const scroller = containerRef.current;
    if (!scroller) return;
    const onScroll = () => {
      const midY = scroller.scrollTop + scroller.clientHeight / 3;
      let best = 1;
      for (let i = 1; i <= numPages; i++) {
        const el = pagesRef.current[i];
        if (!el) continue;
        if (el.offsetTop <= midY) best = i; else break;
      }
      setPageNumber(best);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [isMobile, numPages]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const goToPrevPage = () => {
    if (isMobile) {
      const target = Math.max(pageNumber - 1, 1);
      pagesRef.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setPageNumber((prev) => Math.max(prev - 1, 1));
    }
  };

  const goToNextPage = () => {
    if (isMobile) {
      const target = Math.min(pageNumber + 1, numPages || 1);
      pagesRef.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
    }
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  if (!docPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Document non spécifié</p>
          <Link to="/documents" className="text-sky-600 hover:text-sky-700">
            Retour aux documents
          </Link>
        </div>
      </div>
    );
  }

  // On mobile, fit each page to the container width for optimal reading (no horizontal scroll).
  const mobilePageWidth = Math.min(containerWidth - 8, 900);

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col">
      {!isMobile && <SiteHeader />}

      {/* Toolbar */}
      <div className="bg-slate-800 border-b border-slate-700 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between z-50 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Link
            to={backPath}
            className="flex items-center gap-1.5 sm:gap-2 text-slate-300 hover:text-white transition-colors flex-shrink-0"
            data-testid="doc-viewer-close"
          >
            <X size={20} />
            <span className="hidden sm:inline">Fermer</span>
          </Link>
          <div className="h-6 w-px bg-slate-600 hidden sm:block" />
          <h1 className="text-white font-medium text-xs sm:text-base truncate">
            {decodeURIComponent(title)}
          </h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          {/* Zoom controls — desktop only (mobile pinches / uses browser zoom) */}
          {!isMobile && (
            <>
              <div className="flex items-center gap-1 sm:gap-2">
                <button onClick={zoomOut} className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg" title="Zoom arrière">
                  <ZoomOut size={18} />
                </button>
                <span className="text-slate-400 text-sm min-w-[50px] text-center">{Math.round(scale * 100)}%</span>
                <button onClick={zoomIn} className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg" title="Zoom avant">
                  <ZoomIn size={18} />
                </button>
              </div>
              <div className="h-6 w-px bg-slate-600" />
            </>
          )}

          {/* Page navigation */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-40"
              data-testid="doc-viewer-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-slate-300 text-xs sm:text-sm min-w-[70px] text-center" data-testid="doc-viewer-counter">
              {pageNumber} / {numPages || '...'}
            </span>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 1)}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-40"
              data-testid="doc-viewer-next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer — vertical scroll mode on mobile, single page on desktop */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col items-center py-2 sm:py-8 px-1 sm:px-4"
        style={{ userSelect: 'none', WebkitOverflowScrolling: 'touch' }}
        data-testid="doc-viewer-scroll"
      >
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
          </div>
        )}

        <Document
          file={docPath}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => console.error('Error loading PDF:', error)}
          loading={
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
            </div>
          }
        >
          {isMobile && numPages
            ? Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => (
                <div
                  key={pn}
                  ref={(el) => { pagesRef.current[pn] = el; }}
                  className="mb-3 shadow-xl w-full flex justify-center"
                  data-testid={`doc-viewer-page-${pn}`}
                >
                  <Page
                    pageNumber={pn}
                    width={mobilePageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="bg-white"
                    loading={<div className="w-full bg-white/5 animate-pulse" style={{ height: '60vh' }} />}
                  />
                </div>
              ))
            : (
              <div className="shadow-2xl">
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="bg-white"
                />
              </div>
            )
          }
        </Document>

        {isMobile && numPages && (
          <p className="text-slate-500 text-[11px] mt-2 mb-6">Faites défiler pour lire les {numPages} pages.</p>
        )}
      </div>

      {/* Footer info — hidden on mobile to maximise reading area */}
      {!isMobile && (
        <div className="bg-slate-800 border-t border-slate-700 px-4 py-2 text-center flex-shrink-0">
          <p className="text-slate-500 text-xs">
            Document protégé - Consultation uniquement
          </p>
        </div>
      )}
    </div>
  );
};

export default ProtectedDocumentViewer;
